@import '../../../styles/teamscale_variables';
@import '../../../styles/font_awesome_variables';

@gray: #ccc;

.codeContainerArea {
	scrollbar-width: thin;

	// Without it the diff indicators (borders) show up at the wrong location when the code table is virtualized
	// https://stackoverflow.com/q/49209548
	border-collapse: separate !important;

	padding: 0;
	margin: 12px 0;
	font-family: Consolas, monospace;
	white-space: pre;
	direction: ltr;
	tab-size: 4;
	max-width: none;

	// -2px to account for the borders (otherwise causes scrollbar in container)
	width: calc(100% - 2px);
	overflow: visible;
	display: table;
	border: none;
	background: #fff;

	// We can't use fixed here and hence have to use the slightly slower 'auto' to avoid horizontal overflow
	// which leads to lines being cut off in Safari (TS-39442)
	table-layout: auto;
}

.line-number {
	padding-left: 1em;
	padding-right: 0.25em;
	text-align: right;
	vertical-align: top;
	word-break: normal;
	color: #a2a2a2;
	user-select: none;
	box-sizing: content-box !important;

	&.line-menu-button:hover {
		background-color: #e8e8e8;
		color: #000000;
		cursor: pointer;
	}
}

// These values should be kept in sync with compare/Style.ts
@diff-red-border: #f77;
@diff-green-border: #6e6;
@diff-blue-border: #77f;
@diff-yellow-border-color-blind-mode: #f0e442;
@diff-blue-highlight: #bbe;
@diff-yellow-highlight: #eeec51;

.codeContainerArea {
	--color-fully-covered: @light-green;
	--color-fully-covered-darker: color-mix(in srgb, @light-green 70%, black);
	--color-partially-covered: @light-yellow;
	--color-not-covered: @light-red;

	--color-removed-code-line-border: @diff-red-border;
	--color-added-code-line-border: @diff-green-border;
	--color-changed-code-line-border: @diff-blue-border;
}

.color-blind-mode .codeContainerArea {
	--color-fully-covered: @light-blue;
	--color-fully-covered-darker: color-mix(in srgb, @light-blue 70%, black);
	// --color-partially-covered and --color-not-covered stay the same

	--color-removed-code-line-border: @diff-yellow-border-color-blind-mode;
	// --color-added-code-line-border and --color-changed-code-line-border stay the same
}

.codeContainerArea {
	:global(.partially_covered) td:last-child {
		background-color: @light-yellow;
	}

	:global(.not_covered) td:last-child {
		background-color: var(--color-not-covered);
	}

	:global(.untested_addition) {
		--test-gap-color: @light-red;
		--test-gap-border-color: color-mix(in srgb, @light-red 70%, black);
	}

	:global(.untested_change) {
		--test-gap-color: @light-yellow;
		--test-gap-border-color: color-mix(in srgb, @light-yellow 70%, black);
	}

	:global(.unchanged) {
		--test-gap-color: @gray;
		--test-gap-border-color: color-mix(in srgb, @gray 70%, black);
	}

	:global(.fully_covered) td:last-child {
		background-color: var(--color-fully-covered);
	}

	:global(.tested_churn) {
		--test-gap-color: var(--color-fully-covered);
		--test-gap-border-color: var(--color-fully-covered-darker);
	}

	:global(.code-line .diff-marked-red) {
		background: @light-red;
	}

	:global(.code-line .diff-marked-green) {
		background: @light-green;
	}

	:global(.code-line .diff-marked-blue) {
		background: @light-blue;
	}
}

.test-gap td:last-child {
	background-color: var(--test-gap-color);
	box-shadow:
		inset 2px 0 0 0 var(--test-gap-border-color),
		inset -2px 0 0 0 var(--test-gap-border-color);
}

.test-gap-start td:last-child {
	box-shadow:
		inset 2px 0 0 0 var(--test-gap-border-color),
		inset -2px 0 0 0 var(--test-gap-border-color),
		inset 0 2px 0 0 var(--test-gap-border-color);
}

.test-gap-end td:last-child {
	box-shadow:
		inset 2px 0 0 0 var(--test-gap-border-color),
		inset -2px 0 0 0 var(--test-gap-border-color),
		inset 0 -2px 0 0 var(--test-gap-border-color);
}

.test-gap-start.test-gap-end td:last-child {
	box-shadow:
		inset 2px 0 0 0 var(--test-gap-border-color),
		inset -2px 0 0 0 var(--test-gap-border-color),
		inset 0 2px 0 0 var(--test-gap-border-color),
		inset 0 -2px 0 0 var(--test-gap-border-color);
}

.codeContainerArea {
	:global(.diff-marked-green) {
		--color-diff-line-border: var(--color-added-code-line-border);
	}

	:global(.diff-marked-red) {
		--color-diff-line-border: var(--color-removed-code-line-border);
	}

	:global(.diff-marked-blue) {
		--color-diff-line-border: var(--color-changed-code-line-border);
	}

	:global(.diff-lines-start) td {
		box-shadow: inset 0 1px 0 0 var(--color-diff-line-border);
	}

	:global(.diff-lines-end) td {
		box-shadow: inset 0 -1px 0 0 var(--color-diff-line-border);
	}

	:global(.diff-lines-start):global(.diff-lines-end) td {
		box-shadow:
			inset 0 1px 0 0 var(--color-diff-line-border),
			inset 0 -1px 0 0 var(--color-diff-line-border);
	}

	:global(.diff-marked-blue .diff-highlight) {
		background: @diff-blue-highlight;
		display: inline-block;
	}

	:global(.diff-marked-blue .diff-highlight-secondary) {
		background: @diff-yellow-highlight;
		display: inline-block;
	}
}

.code-line-marked td:not(:last-child) {
	background: #eaf2fc;
}

.code-line-marked-refactoring {
	background-color: #ffffe0;
}

.finding-marker-area {
	width: 100%;
	height: 100%;
}

.finding-marker {
	position: absolute;
	inset-block: 0;
	width: 2px;

	&.active {
		background-color: @tsRed;
	}

	&.yellow {
		background-color: @tsYellowReadableOnBrightBackground;
	}

	&.flagged {
		background-color: @grayLight;
	}

	&.start {
		margin-top: 2px;
		border-top-left-radius: 1px;
		border-top-right-radius: 1px;
	}

	&.end {
		margin-bottom: 2px;
		border-bottom-left-radius: 1px;
		border-bottom-right-radius: 1px;
	}
}

@code-highlight-color: rgba(36, 114, 255, 0.38);
.code-highlight {
	background-color: @code-highlight-color;
}

.codeContainerArea {
	:global(.code-input-param-highlight) {
		background-color: #b6f6bc;
	}

	:global(.code-return-param-highlight) {
		background-color: #dadaff;
	}

	:global(.code-input-param-highlight.code-return-param-highlight) {
		background-color: #c7fffa;
	}

	:global(.code-statement-path-highlight) td:last-child {
		display: inline-flex;
		padding-left: 7px;
		padding-right: 7px;
		background-color: rgba(213, 204, 250, 0.54);
		border-radius: 3px;
		box-shadow: 2px 0 1px 0 #969083;
	}

	:global(.source.code-statement-path-highlight) td:last-child {
		background-color: rgba(178, 241, 235, 0.74) !important;
	}

	:global(.sink.code-statement-path-highlight) td:last-child {
		background-color: rgba(226, 110, 110, 0.74) !important;
	}
}

.dataflow-statement-description {
	margin-left: 20px;
	background-color: @tsYellow;
	color: #333;
	fill-opacity: 0.2;
	font-weight: 600;
	box-shadow: 2px 1px 3px 0 #969083;
	border-radius: 3px;
	padding-left: 5px;
	padding-right: 5px;

	.number {
		font-size: 1em;
		margin: 2px 5px 2px 2px;
		padding: 1px 3px;
		background-color: #fff;
		border-radius: 3px;
		box-shadow: 0.5px 0.6px 2px black;
	}
}
